/**
 * The console's theme: the operating system's by default, or one the person picks, remembered on
 * this browser. A pick sets `data-theme` on the root, which `@rythmo/tokens`' `tokens.css` and the
 * console's own styles obey over `prefers-color-scheme`.
 *
 * `public/theme.js` applies the stored pick before the first paint, with the same key and values;
 * the CSP allows no inline script, so it is a file of its own.
 */
export const THEMES = ['system', 'light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];

export const THEME_KEY = 'rythmo.admin.theme';

export function readTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  if (theme === 'system') {
    delete root.dataset['theme'];
    root.style.removeProperty('color-scheme');
  } else {
    root.dataset['theme'] = theme;
    // Native parts (scrollbars, form controls) follow the pick too.
    root.style.setProperty('color-scheme', theme);
  }
}

export function storeTheme(theme: Theme): void {
  try {
    if (theme === 'system') window.localStorage.removeItem(THEME_KEY);
    else window.localStorage.setItem(THEME_KEY, theme);
  } catch {
    // A private window may refuse storage; the pick still holds for this visit.
  }
}
